import { FC, useEffect, useState } from 'react';
import { useCompetitionContext } from '../providers/CompetitionContext';
import { getCompetitionCopy } from '../lib/competitionCopy';

interface TeamStatsProps {
  teamId: number | string;
  weekId: number;
}

interface RankData {
  teamId: number;
  teamName: string;
  rank: number;
  points: number;
}

interface TeamStatsData {
  generalRank: RankData;
  weekRank: RankData;
}

const TeamStats: FC<TeamStatsProps> = ({ teamId, weekId }) => {
  const { competitionConfig } = useCompetitionContext();
  const copy = getCompetitionCopy(competitionConfig?.competitionFeed);
  const [statsData, setStatsData] = useState<TeamStatsData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teamId || !competitionConfig?.seasonId || !competitionConfig?.competitionFeed) return;

    const abortController = new AbortController();

    const fetchStats = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const url = `/api/rankings/user?userId=${teamId}&competitionFeed=${competitionConfig.competitionFeed}&seasonId=${competitionConfig.seasonId}&weekId=${weekId}`;
        const response = await fetch(url, { signal: abortController.signal });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch stats: ${response.statusText}`);
        }
        
        const data: TeamStatsData = await response.json();
        setStatsData(data);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.error('Error fetching team stats:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchStats();

    return () => abortController.abort();
  }, [teamId, weekId, competitionConfig?.competitionFeed, competitionConfig?.seasonId]);

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-red-50 text-red-600 p-4 rounded-lg">
        <p>Error loading statistics: {error}</p>
      </div>
    );
  }

  if (!statsData) {
    return null;
  }

  const statCards = [
    {
      label: `Points ${copy.raceLabelTitle}`,
      value: statsData.weekRank?.points ? statsData.weekRank.points : '-'
    },
    {
      label: `Classement ${copy.raceLabelTitle}`,
      value: statsData.weekRank?.rank ? statsData.weekRank.rank : '-'
    },
    {
      label: "Points Général",
      value: statsData.generalRank?.points ? statsData.generalRank.points : '-'
    },
    {
      label: "Classement Général",
      value: statsData.generalRank?.rank ? statsData.generalRank.rank : '-'
    }
  ];

  return (
    <div className="w-full bg-white rounded-b-lg p-4 mb-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((card, index) => (
          <div key={index} className="bg-gray-50 rounded-lg shadow p-4 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold">{card.value}</div>
            <div className="text-sm text-gray-500 mt-1">{card.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamStats;
